//REACT
import { useState, useEffect, useMemo } from 'react';

//MUI
import { IconButton } from '@mui/material';

//STYLES
import {
    TriggerButton,
    Overlay,
    ModalChatContainer,
    ChatHeader,
    HeaderInfo,
    HeaderActions,
    LoadingContainer,
    EmptyChatMessage,
} from './styles/CommentModalStyles';

//COMPONENTS
import Text from '../../common/Text';
import CommentsChat from './CommentsChat';
import FontAwesomeIcon from '../../common/FontAwesomeIcon';
import SimpleLoading from '../../common/SimpleLoading';

//UTILS
import {
    formatCommentDate,
    formatCommentDateTime,
    getCommentSortMs,
} from '../../../utils/dates/dates.utils';
import {
    buildLegacyCommentsFromSubmission,
    mergeLegacyAndConversationComments,
    isAdiProfessorEmail,
} from './legacyComments.utils';

//REDUX
import {
    searchConversations,
    getConversationsResource,
    getConversationResource,
    postCommentResource,
} from '../../../redux/api/comments';

const pickAttachmentUrl = (v) =>
    v && typeof v === 'object' && v.url ? v.url : null;

const TeacherCommentsModal = (props) => {
    const {
        buttonLabel,
        resourceId,
        studentId,
        teacherId,
        subjectId,
        learningObjectId,
        legacySubmission = null,
        legacyStudent = null,
        legacyProfessor = null,
        shouldOpen = false,
        onOpenChange,
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [resourceName, setResourceName] = useState('');
    const [conversationId, setConversationId] = useState(null);
    const legacyComments = useMemo(
        () =>
            buildLegacyCommentsFromSubmission({
                submission: legacySubmission,
                student: legacyStudent,
                professor: legacyProfessor,
            }),
        [legacyProfessor, legacyStudent, legacySubmission]
    );

    const legacyMergeOptions = useMemo(
        () => ({
            submissionId:
                legacySubmission?.id ?? legacySubmission?.submission_id ?? null,
            isAdiProfessor: isAdiProfessorEmail(
                legacyProfessor?.email ?? legacySubmission?.professor?.email
            ),
        }),
        [legacyProfessor, legacySubmission]
    );

    useEffect(() => {
        if (shouldOpen && resourceId && !isOpen) {
            setIsOpen(true);
            if (onOpenChange) {
                onOpenChange(false);
            }
        }
    }, [shouldOpen, resourceId, isOpen, onOpenChange]);

    useEffect(() => {
        if (isOpen && resourceId) {
            document.body.style.overflow = 'hidden';
            loadConversation();
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen, resourceId, studentId]);

    const transformMessagesToComments = (messages) => {
        if (!messages || !Array.isArray(messages) || messages.length === 0) {
            return [];
        }
        return messages.map((messageItem) => {
            const att = messageItem.attachments || {};
            const fileUrl =
                pickAttachmentUrl(att.file) ||
                (messageItem.attachment && messageItem.attachment.url
                    ? messageItem.attachment.url
                    : null);
            const submissionIdNum = Number(messageItem.submission_id);
            return {
                id: messageItem.id,
                name: messageItem.author_name,
                comment: messageItem.message,
                date:
                    formatCommentDate(messageItem.create_date) +
                    ' ' +
                    formatCommentDateTime(messageItem.create_date),
                photo: messageItem.author_picture || null,
                sender: messageItem.author_is_teacher ? 'user' : 'other',
                author_is_teacher: messageItem.author_is_teacher,
                comment_type: messageItem.comment_type || 'normal',
                submission_id: Number.isNaN(submissionIdNum)
                    ? null
                    : submissionIdNum,
                sort_ms: getCommentSortMs(messageItem.create_date),
                image: fileUrl,
                attachments: {
                    fileUrl,
                    audioUrl: pickAttachmentUrl(att.audio),
                    videoUrl: pickAttachmentUrl(att.video),
                },
            };
        });
    };

    const loadConversation = async () => {
        if (!resourceId || !studentId) return;
        const response = await getConversationsResource(resourceId);

        try {
            setIsLoading(true);

            if (teacherId) {
                const response = await searchConversations(
                    studentId,
                    resourceId,
                    learningObjectId,
                    subjectId
                );

                if (response && !response.error) {
                    setResourceName(response.resource_name || '');
                    if (response.messages && Array.isArray(response.messages)) {
                        setConversationId(response.id || null);
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                transformMessagesToComments(response.messages),
                                legacyMergeOptions
                            )
                        );
                    } else if (response.conversation) {
                        setConversationId(response.conversation.id);
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                transformMessagesToComments(
                                    response.conversation.messages
                                ),
                                legacyMergeOptions
                            )
                        );
                    } else if (
                        response.conversations &&
                        Array.isArray(response.conversations) &&
                        response.conversations.length > 0
                    ) {
                        const conv = response.conversations[0];
                        setConversationId(conv.id);
                        if (conv.messages) {
                            setComments(
                                mergeLegacyAndConversationComments(
                                    legacyComments,
                                    transformMessagesToComments(conv.messages),
                                    legacyMergeOptions
                                )
                            );
                        } else {
                            const details = await getConversationResource(
                                conv.id
                            );
                            if (details && !details.error && details.messages) {
                                setComments(
                                    mergeLegacyAndConversationComments(
                                        legacyComments,
                                        transformMessagesToComments(
                                            details.messages
                                        ),
                                        legacyMergeOptions
                                    )
                                );
                            } else {
                                setComments(
                                    mergeLegacyAndConversationComments(
                                        legacyComments,
                                        [],
                                        legacyMergeOptions
                                    )
                                );
                            }
                        }
                    } else {
                        setConversationId(null);
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                [],
                                legacyMergeOptions
                            )
                        );
                    }
                } else {
                    setConversationId(null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            [],
                            legacyMergeOptions
                        )
                    );
                }
            } else {
                const response = await getConversationsResource(resourceId);

                if (response && !response.error) {
                    setResourceName(response.resource_name || '');

                    if (
                        response.conversations &&
                        Array.isArray(response.conversations) &&
                        response.conversations.length > 0
                    ) {
                        const foundConversation = response.conversations.find(
                            (conv) => conv.student_id == studentId
                        );

                        if (foundConversation) {
                            setConversationId(foundConversation.id);

                            const conversationDetails =
                                await getConversationResource(
                                    foundConversation.id
                                );

                            if (
                                conversationDetails &&
                                !conversationDetails.error
                            ) {
                                setComments(
                                    mergeLegacyAndConversationComments(
                                        legacyComments,
                                        transformMessagesToComments(
                                            conversationDetails.messages
                                        ),
                                        legacyMergeOptions
                                    )
                                );
                            } else {
                                setComments(
                                    mergeLegacyAndConversationComments(
                                        legacyComments,
                                        [],
                                        legacyMergeOptions
                                    )
                                );
                            }
                        } else {
                            setConversationId(null);
                            setComments(
                                mergeLegacyAndConversationComments(
                                    legacyComments,
                                    [],
                                    legacyMergeOptions
                                )
                            );
                        }
                    } else {
                        setConversationId(null);
                        setComments(
                            mergeLegacyAndConversationComments(
                                legacyComments,
                                [],
                                legacyMergeOptions
                            )
                        );
                    }
                } else if (response && response.error) {
                    console.error(
                        'Error al obtener las conversaciones:',
                        response.error
                    );
                    setConversationId(null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            [],
                            legacyMergeOptions
                        )
                    );
                } else {
                    setConversationId(null);
                    setComments(
                        mergeLegacyAndConversationComments(
                            legacyComments,
                            [],
                            legacyMergeOptions
                        )
                    );
                }
            }
        } catch (error) {
            console.error('Error al obtener las conversaciones:', error);
            setConversationId(null);
            setComments(
                mergeLegacyAndConversationComments(
                    legacyComments,
                    [],
                    legacyMergeOptions
                )
            );
        } finally {
            setIsLoading(false);
        }
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                if (file.size >= 5242880) {
                    alert('El tamaño máximo permitido es 5MB');
                    e.target.value = '';
                    return;
                }
                setSelectedImage(file);
                const reader = new FileReader();
                reader.onloadend = () => {
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const handleSendMessage = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (!comment.trim()) return;

        if (!conversationId) {
            alert(
                'Los profesores no pueden crear nuevas conversaciones. Solo pueden responder a conversaciones existentes.'
            );
            setIsSendingMessage(false);
            return;
        }

        setIsSendingMessage(true);

        try {
            const formData = new FormData();
            formData.append('message', comment);
            formData.append('student_id', String(studentId));
            formData.append('resource_id', String(resourceId));
            if (selectedImage instanceof File) {
                formData.append('attachment', selectedImage);
            }

            const response = await postCommentResource(
                conversationId,
                formData
            );

            if (response && !response.error && response.id) {
                const resAtt = response.attachments || {};
                const fileUrl =
                    pickAttachmentUrl(resAtt.file) ||
                    (response.attachment &&
                    response.attachment !== false &&
                    response.attachment.url
                        ? response.attachment.url
                        : null);
                const newComment = {
                    id: response.id,
                    name: response.author_name || '',
                    comment: response.message || comment,
                    date: response.create_date
                        ? formatCommentDate(response.create_date) +
                          ' ' +
                          formatCommentDateTime(response.create_date)
                        : new Date().toLocaleDateString('es-ES', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                          }) +
                          ' ' +
                          new Date().toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                          }) +
                          ' hs',
                    photo: response.author_picture || null,
                    sender: response.author_is_teacher ? 'user' : 'other',
                    author_is_teacher: response.author_is_teacher,
                    comment_type: response.comment_type || 'normal',
                    image: fileUrl,
                    attachments: {
                        fileUrl,
                        audioUrl: pickAttachmentUrl(resAtt.audio),
                        videoUrl: pickAttachmentUrl(resAtt.video),
                    },
                    sort_ms: response.create_date
                        ? getCommentSortMs(response.create_date)
                        : Date.now(),
                };

                setComments((prevComments) => [...prevComments, newComment]);
                setComment('');
                setSelectedImage(null);
                setImagePreview(null);
            } else {
                console.error(
                    'Error al enviar el comentario:',
                    response?.error || 'Respuesta inválida'
                );
            }
        } catch (error) {
            console.error('Error al enviar el comentario:', error);
        } finally {
            setIsSendingMessage(false);
        }
    };

    return (
        <>
            <TriggerButton onClick={toggleChat} aria-label="Abrir comentarios">
                <FontAwesomeIcon icon="fa-light fa-comments" />
                {buttonLabel && <Text fontSize="16px">{buttonLabel}</Text>}
            </TriggerButton>
            {isOpen && (
                <>
                    <Overlay onClick={toggleChat} />
                    <ModalChatContainer>
                        <ChatHeader>
                            <HeaderInfo>
                                <p>
                                    Comentarios sobre esta actividad
                                    <span>
                                        {resourceName && ` - ${resourceName}`}
                                    </span>
                                </p>
                            </HeaderInfo>
                            <HeaderActions>
                                <IconButton
                                    onClick={toggleChat}
                                    size="small"
                                    sx={{ color: '#000' }}
                                >
                                    <FontAwesomeIcon icon="fa-light fa-xmark" />
                                </IconButton>
                            </HeaderActions>
                        </ChatHeader>

                        {isLoading ? (
                            <LoadingContainer>
                                <SimpleLoading />
                            </LoadingContainer>
                        ) : (
                            <>
                                {comments.length === 0 && !conversationId && (
                                    <EmptyChatMessage>
                                        <Text fontSize="16px" color="#616161">
                                            No hay conversaciones existentes.
                                            Solo los estudiantes pueden iniciar
                                            nuevas conversaciones.
                                        </Text>
                                    </EmptyChatMessage>
                                )}
                                {comments.length === 0 && conversationId && (
                                    <EmptyChatMessage>
                                        <Text fontSize="16px" color="#616161">
                                            No hay comentarios. Sé el primero en
                                            escribir.
                                        </Text>
                                    </EmptyChatMessage>
                                )}
                                <CommentsChat
                                    comments={comments}
                                    imagePreview={imagePreview}
                                    handleRemoveImage={handleRemoveImage}
                                    handleImageChange={handleImageChange}
                                    handleSendMessage={handleSendMessage}
                                    comment={comment}
                                    setComment={setComment}
                                    isLoading={isSendingMessage}
                                    selectedImage={selectedImage}
                                />
                            </>
                        )}
                    </ModalChatContainer>
                </>
            )}
        </>
    );
};

export default TeacherCommentsModal;
